import type { TableColumnType } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import type {
    TabelaPrecoProduto,
    TabelaPrecoProdutoExcecao,
} from '@/features/tabela-preco/types/TabelaPrecoProduto';
import type { Dialog } from '@/std/dialog/types';
import { TableActionsInline } from '@/components/table/table-actions/TableActionsInline';
import { TagPacote, TagProduto } from '@/features/produto/components/tag';
import {
    TabelaPrecoExcecaoTipoPreco,
    tabelaPrecoTipoToStr,
    type TabelaPrecoTipoVariacao,
} from '@/features/tabela-preco/types/enum';
import { numberToCurrency, numberToPercent } from '@/std/format';
import { unreachable } from '@/std/util';

export function produtoTabTabelaPrecoColumns(
    dialog: Dialog<TabelaPrecoProduto>,
): TableColumnType<TabelaPrecoProduto>[] {
    return [
        {
            dataIndex: 'tap_descricao',
            title: 'Tabela de preço',
        },
        {
            align: 'center',
            key: 'tipo',
            render: (_, row) => {
                return row.produto_pacote_barra ? <TagPacote /> : <TagProduto />;
            },
            title: 'Tipo',
            width: '6%',
        },
        {
            key: 'produto',
            render: (_, row) => {
                return row.produto_pacote_barra
                    ? `${row.produto?.pro_descricao} - ${row.produto_pacote_barra.pcb_descricao}`
                    : row.produto?.pro_descricao || '';
            },
            title: 'Produto / Pacote de barras',
        },
        {
            align: 'center',
            dataIndex: 'tap_tipo_variacao',
            render: (tipo_preco: TabelaPrecoTipoVariacao, row) =>
                `${tabelaPrecoTipoToStr(tipo_preco)} ${numberToPercent(row.tap_percentual ?? 0)}`,
            title: 'Tipo',
        },
        {
            align: 'center',
            dataIndex: 'tabela_preco_excecao',
            render: (excecao: TabelaPrecoProdutoExcecao | null, row) => {
                if (!excecao) {
                    return '-';
                }

                switch (excecao.tpe_tipo_preco) {
                    case TabelaPrecoExcecaoTipoPreco.PrecoEspecificado: {
                        return `Preço especificado: ${numberToCurrency(excecao.tpe_preco)}`;
                    }
                    case TabelaPrecoExcecaoTipoPreco.PrecoProdutoOuPacoteBarras: {
                        if (row.produto_pacote_barra) {
                            return 'Preço do pacote de barras';
                        } else {
                            return 'Preço do produto';
                        }
                    }
                    case TabelaPrecoExcecaoTipoPreco.PercentualAcrescimo:
                        return `Percentual de acréscimo: ${numberToPercent(excecao.tpe_preco)}`;
                    case TabelaPrecoExcecaoTipoPreco.PercentualDesconto:
                        return `Percentual de desconto: ${numberToPercent(excecao.tpe_preco)}`;
                    case TabelaPrecoExcecaoTipoPreco.ValorAcrescimo:
                        return `Valor de acréscimo: ${numberToCurrency(excecao.tpe_preco)}`;
                    case TabelaPrecoExcecaoTipoPreco.ValorDesconto:
                        return `Valor de desconto: ${numberToCurrency(excecao.tpe_preco)}`;
                    default:
                        unreachable(excecao.tpe_tipo_preco);
                }
            },
            title: 'Exceção',
        },
        {
            align: 'right',
            dataIndex: 'preco_calculado',
            render: (preco_calculado: number) => numberToCurrency(preco_calculado),
            title: 'Preço calculado',
        },
        {
            align: 'center',
            key: 'edit',
            render: (_, row) => (
                <TableActionsInline
                    buttons={[
                        {
                            icon: <EditOutlined />,
                            id: 'editar',
                            onClick: () => dialog.actions.open(row),
                        },
                    ]}
                />
            ),
            title: 'Editar',
            width: 40,
        },
    ];
}
